import { useEffect, useRef, useState, type FormEvent, type ReactElement, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  LICENCE_LEVELS,
  LICENCE_RATINGS,
  MEMBERSHIP_TYPES,
  type FeeSchedule,
  type LicenceRating,
  type MembershipApplicationInput,
  type MembershipInfo,
  type MembershipType,
} from '@jupi/shared'
import { ApiError, BrandLogo, LanguageSelector, RemoteContent, api, useThemeMode } from '@jupi/ui'
import { SUPPORTED_LOCALES } from '@jupi/ui/i18n'
import { Turnstile, type TurnstileHandle } from '@jupi/ui'

// The public membership application form -- no login. The club's website
// links here (?lang=en|sv|fi picks the language). Replaces the WordPress
// /register/ form. Spam is kept out server-side: Turnstile, a honeypot
// field, a tight per-IP rate limit (routes/publicMembership.ts).

const CLUB = 'Jukolan Pilotit ry'
const WEBSITE = 'https://www.jukolanpilotit.fi/'
const EVENTS_CALENDAR = 'https://www.jukolanpilotit.fi/events/'
const SIL = 'https://ilmailuliitto.fi/'
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
const STEPS = ['about', 'details', 'background'] as const

interface Form {
  membershipType: MembershipType | ''
  firstName: string
  lastName: string
  dateOfBirth: string
  email: string
  phone: string
  address: string
  postalCode: string
  city: string
  licenceLevel: string
  licenceRatings: LicenceRating[]
  licenceExpiredYear: string
  trainingDetails: string
  flightHours: string
  additionalInfo: string
  acceptRules: boolean
  acceptPrivacy: boolean
  website: string
}

const EMPTY: Form = {
  membershipType: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  licenceLevel: '',
  licenceRatings: [],
  licenceExpiredYear: '',
  trainingDetails: '',
  flightHours: '',
  additionalInfo: '',
  acceptRules: false,
  acceptPrivacy: false,
  website: '',
}

// Which step shows each field, so a server-side error can take the
// applicant back to it.
const DETAILS_FIELDS: (keyof Form)[] = [
  'membershipType',
  'firstName',
  'lastName',
  'dateOfBirth',
  'email',
  'phone',
  'address',
  'postalCode',
  'city',
]

type Errors = Partial<Record<keyof Form, string>>

const today = () => new Date().toISOString().slice(0, 10)

export default function JoinPage() {
  const { t, i18n } = useTranslation()
  const { mode } = useThemeMode()
  const [params, setParams] = useSearchParams()
  const [step, setStep] = useState(0)
  const [form, setForm] = useState<Form>(EMPTY)
  const [errors, setErrors] = useState<Errors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>()
  const turnstileRef = useRef<TurnstileHandle>(null)
  const topRef = useRef<HTMLDivElement>(null)

  // ?lang= from the club's website wins over the browser's language -- once.
  // It is then dropped from the URL, or it would put the language back every
  // time the page re-rendered after the applicant picked another.
  const lang = params.get('lang')
  useEffect(() => {
    if (!lang) return
    if ((SUPPORTED_LOCALES as readonly string[]).includes(lang)) void i18n.changeLanguage(lang)
    setParams(
      (p) => {
        p.delete('lang')
        return p
      },
      { replace: true },
    )
  }, [lang, i18n, setParams])

  useEffect(() => {
    document.title = t('join.title', { club: CLUB })
  }, [t, i18n.language])

  const { data: info, isLoading, error } = useQuery({
    queryKey: ['public', 'membership'],
    queryFn: () => api.get<MembershipInfo>('/api/public/membership'),
  })

  const goTo = (next: number) => {
    setStep(next)
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setErrors((e) => ({ ...e, [key]: undefined }))
  }

  const validateDetails = (): Errors => {
    const e: Errors = {}
    for (const key of DETAILS_FIELDS) if (!String(form[key]).trim()) e[key] = t('join.required')
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = t('join.invalidEmail')
    if (form.phone && !/^[+\d][\d\s()-]{4,}$/.test(form.phone.trim())) e.phone = t('join.invalidPhone')
    if (form.dateOfBirth && (form.dateOfBirth < '1900-01-01' || form.dateOfBirth > today())) {
      e.dateOfBirth = t('join.invalidDate')
    }
    return e
  }

  const validateBackground = (): Errors => {
    const e: Errors = {}
    if (!form.acceptRules) e.acceptRules = t('join.required')
    if (!form.acceptPrivacy) e.acceptPrivacy = t('join.required')
    return e
  }

  const next = () => {
    const e = validateDetails()
    setErrors(e)
    if (Object.keys(e).length === 0) goTo(2)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const e = { ...validateDetails(), ...validateBackground() }
    setErrors(e)
    if (Object.keys(e).length > 0) {
      if (DETAILS_FIELDS.some((k) => e[k])) goTo(1)
      return
    }

    const body: MembershipApplicationInput = {
      membershipType: form.membershipType as MembershipType,
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth,
      email: form.email,
      phone: form.phone,
      address: form.address,
      postalCode: form.postalCode,
      city: form.city,
      licenceLevel: (form.licenceLevel || null) as MembershipApplicationInput['licenceLevel'],
      licenceRatings: form.licenceRatings,
      licenceExpiredYear: form.licenceExpiredYear ? Number(form.licenceExpiredYear) : null,
      trainingDetails: form.trainingDetails || null,
      flightHours: form.flightHours ? Number(form.flightHours.replace(',', '.')) : null,
      additionalInfo: form.additionalInfo || null,
      locale: (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language.slice(0, 2))
        ? (i18n.language.slice(0, 2) as 'fi' | 'sv' | 'en')
        : 'fi',
      acceptRules: true,
      acceptPrivacy: true,
      turnstileToken,
      website: form.website || undefined,
    }

    setBusy(true)
    setSubmitError(null)
    try {
      await api.post('/api/public/membership/applications', body)
      setSentTo(form.email.trim())
      topRef.current?.scrollIntoView({ block: 'start' })
    } catch (err) {
      turnstileRef.current?.reset()
      setTurnstileToken(undefined)
      setSubmitError(errorMessage(err))
      if (err instanceof ApiError && Object.keys(err.fieldErrors).length > 0) {
        const fieldErrors: Errors = {}
        for (const key of Object.keys(err.fieldErrors)) fieldErrors[key as keyof Form] = t('join.failed')
        setErrors(fieldErrors)
        if (DETAILS_FIELDS.some((k) => fieldErrors[k])) goTo(1)
      }
    } finally {
      setBusy(false)
    }
  }

  const errorMessage = (err: unknown) => {
    if (!(err instanceof ApiError)) return t('join.failed')
    if (err.status === 409) return t('join.duplicate')
    if (err.status === 429) return err.message.startsWith('Bot check') ? t('join.botCheckFailed') : t('join.tooMany')
    return t('join.failed')
  }

  const field = (key: keyof Form) => ({
    value: form[key] as string,
    onChange: (e: { target: { value: string } }) => set(key, e.target.value as never),
    error: Boolean(errors[key]),
    helperText: errors[key],
    fullWidth: true,
  })

  const rulesLink: ReactElement | null = info?.rulesUrl ? (
    <Link href={info.rulesUrl} target="_blank" rel="noopener" />
  ) : null

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Box ref={topRef} sx={{ maxWidth: 760, mx: 'auto', scrollMarginTop: 16 }}>
        <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 2 }}>
          <LanguageSelector label={t('join.language')} />
        </Stack>

        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', mb: 3 }}>
              <BrandLogo onDark={mode === 'dark'} height={40} sx={{ maxWidth: '100%' }} />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                {t('join.title', { club: CLUB })}
              </Typography>
            </Stack>

            {sentTo ? (
              <Done email={sentTo} />
            ) : (
              <RemoteContent isLoading={isLoading} error={error}>
                <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, display: { xs: 'none', sm: 'flex' } }}>
                  {STEPS.map((s) => (
                    <Step key={s}>
                      <StepLabel>{t(`join.steps.${s}`)}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Typography variant="overline" color="text.secondary" component="p">
                  {t('join.stepOf', { step: step + 1, total: STEPS.length })}
                </Typography>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  {t(`join.steps.${STEPS[step]}`)}
                </Typography>

                <form onSubmit={submit} noValidate>
                  {step === 0 ? (
                    <AboutStep info={info} rulesLink={rulesLink} onStart={() => goTo(1)} />
                  ) : null}

                  {step === 1 ? (
                    <Stack spacing={3}>
                      <FormControl error={Boolean(errors.membershipType)} required>
                        <FormLabel sx={{ mb: 1 }}>{t('join.fields.membershipType')}</FormLabel>
                        <RadioGroup
                          value={form.membershipType}
                          onChange={(e) => set('membershipType', e.target.value as MembershipType)}
                        >
                          <Stack spacing={1}>
                            {MEMBERSHIP_TYPES.map((type) => (
                              <TypeOption
                                key={type}
                                type={type}
                                fees={info?.fees ?? null}
                                selected={form.membershipType === type}
                              />
                            ))}
                          </Stack>
                        </RadioGroup>
                        {errors.membershipType ? <FormHelperText>{errors.membershipType}</FormHelperText> : null}
                      </FormControl>

                      <Section title={t('join.personal')}>
                        <TextField required label={t('join.fields.firstName')} autoComplete="given-name" {...field('firstName')} />
                        <TextField required label={t('join.fields.lastName')} autoComplete="family-name" {...field('lastName')} />
                        <TextField
                          required
                          type="date"
                          label={t('join.fields.dateOfBirth')}
                          autoComplete="bday"
                          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: '1900-01-01', max: today() } }}
                          {...field('dateOfBirth')}
                        />
                      </Section>

                      <Section title={t('join.contact')}>
                        <TextField required type="email" label={t('join.fields.email')} autoComplete="email" {...field('email')} />
                        <TextField required type="tel" label={t('join.fields.phone')} autoComplete="tel" {...field('phone')} />
                        <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                          <TextField required label={t('join.fields.address')} autoComplete="street-address" {...field('address')} />
                        </Box>
                        <TextField required label={t('join.fields.postalCode')} autoComplete="postal-code" {...field('postalCode')} />
                        <TextField required label={t('join.fields.city')} autoComplete="address-level2" {...field('city')} />
                      </Section>

                      <Nav onBack={() => goTo(0)}>
                        <Button variant="contained" size="large" onClick={next} endIcon={<Icon icon="mdi:arrow-right" />}>
                          {t('join.next')}
                        </Button>
                      </Nav>
                    </Stack>
                  ) : null}

                  {step === 2 ? (
                    <Stack spacing={3}>
                      <Section title={t('join.licence')}>
                        <TextField select label={t('join.fields.licenceLevel')} {...field('licenceLevel')}>
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {LICENCE_LEVELS.map((level) => (
                            <MenuItem key={level} value={level}>
                              {t(`join.levels.${level}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          type="number"
                          label={t('join.fields.licenceExpiredYear')}
                          helperText={t('join.fields.licenceExpiredYearHelp')}
                          slotProps={{ htmlInput: { min: 1950, max: new Date().getFullYear(), inputMode: 'numeric' } }}
                          value={form.licenceExpiredYear}
                          onChange={(e) => set('licenceExpiredYear', e.target.value)}
                          fullWidth
                        />
                        <FormControl component="fieldset" sx={{ gridColumn: { sm: '1 / -1' } }}>
                          <FormLabel component="legend">{t('join.fields.licenceRatings')}</FormLabel>
                          <FormGroup row>
                            {LICENCE_RATINGS.map((rating) => (
                              <FormControlLabel
                                key={rating}
                                label={t(`join.ratings.${rating}`)}
                                control={
                                  <Checkbox
                                    checked={form.licenceRatings.includes(rating)}
                                    onChange={(e) =>
                                      set(
                                        'licenceRatings',
                                        e.target.checked
                                          ? [...form.licenceRatings, rating]
                                          : form.licenceRatings.filter((r) => r !== rating),
                                      )
                                    }
                                  />
                                }
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Section>

                      <Section title={t('join.experience')}>
                        <TextField
                          label={t('join.fields.trainingDetails')}
                          helperText={t('join.fields.trainingDetailsHelp')}
                          slotProps={{ htmlInput: { maxLength: 200 } }}
                          value={form.trainingDetails}
                          onChange={(e) => set('trainingDetails', e.target.value)}
                          fullWidth
                        />
                        <TextField
                          type="number"
                          label={t('join.fields.flightHours')}
                          slotProps={{ htmlInput: { min: 0, step: 0.1, inputMode: 'decimal' } }}
                          value={form.flightHours}
                          onChange={(e) => set('flightHours', e.target.value)}
                          fullWidth
                        />
                      </Section>

                      <TextField
                        multiline
                        minRows={3}
                        label={t('join.fields.additionalInfo')}
                        slotProps={{ htmlInput: { maxLength: 4000 } }}
                        value={form.additionalInfo}
                        onChange={(e) => set('additionalInfo', e.target.value)}
                        fullWidth
                      />

                      {/* Honeypot: off-screen and out of the tab order, so
                          only a bot filling in every input finds it. */}
                      <Box aria-hidden="true" sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                        <label>
                          {t('join.honeypot')}
                          <input
                            type="text"
                            name="website"
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={(e) => set('website', e.target.value)}
                          />
                        </label>
                      </Box>

                      <Stack spacing={1}>
                        <Acceptance
                          checked={form.acceptRules}
                          onChange={(v) => set('acceptRules', v)}
                          error={errors.acceptRules}
                          label={
                            rulesLink ? (
                              <Trans i18nKey="join.acceptRules" components={{ rules: rulesLink }} />
                            ) : (
                              t('join.acceptRulesNoLink')
                            )
                          }
                        />
                        <Acceptance
                          checked={form.acceptPrivacy}
                          onChange={(v) => set('acceptPrivacy', v)}
                          error={errors.acceptPrivacy}
                          label={
                            <>
                              {t('join.acceptPrivacy', { club: CLUB })}
                              {info?.privacyUrl ? (
                                <>
                                  {' '}
                                  <Link href={info.privacyUrl} target="_blank" rel="noopener">
                                    {t('join.privacyLink')}
                                  </Link>
                                </>
                              ) : null}
                            </>
                          }
                        />
                      </Stack>

                      {TURNSTILE_SITE_KEY ? (
                        <Turnstile
                          ref={turnstileRef}
                          siteKey={TURNSTILE_SITE_KEY}
                          onToken={setTurnstileToken}
                          onExpire={() => setTurnstileToken(undefined)}
                          onError={() => setTurnstileToken(undefined)}
                        />
                      ) : null}

                      {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                      <Nav onBack={() => goTo(1)}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={busy || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)}
                          startIcon={<Icon icon="mdi:send" />}
                        >
                          {busy ? t('join.submitting') : t('join.submit')}
                        </Button>
                      </Nav>
                    </Stack>
                  ) : null}
                </form>
              </RemoteContent>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

function useMoney() {
  const { i18n } = useTranslation()
  return (amount: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount)
}

const feesOf = (fees: FeeSchedule, type: MembershipType) => ({
  annual: fees[`${type}AnnualFee`],
  joining: fees[`${type}JoiningFee`],
})

// The joining process as the club's website describes it
// (jukolanpilotit.fi/liittyminen/).
const REQUIREMENTS = ['rules', 'regulations', 'property'] as const
const PROCESS = ['send', 'meeting', 'decision', 'fees', 'clubCheck'] as const

function AboutStep({
  info,
  rulesLink,
  onStart,
}: {
  info: MembershipInfo | undefined
  rulesLink: ReactElement | null
  onStart: () => void
}) {
  const { t } = useTranslation()
  const money = useMoney()
  const fees = info?.fees ?? null
  return (
    <Stack spacing={2}>
      <Typography>{t('join.intro', { club: CLUB })}</Typography>

      <Box>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {t('join.requirementsTitle')}
        </Typography>
        <Box component="ul" sx={{ my: 0.5, pl: 3 }}>
          {REQUIREMENTS.map((key) => (
            <li key={key}>
              <Typography>{t(`join.requirements.${key}`)}</Typography>
            </li>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {t('join.processTitle')}
        </Typography>
        <Box component="ol" sx={{ my: 0.5, pl: 3, '& > li': { mb: 1 } }}>
          {PROCESS.map((key) => (
            <li key={key}>
              <Typography>{t(`join.process.${key}`)}</Typography>
              {key === 'meeting' ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <Trans
                    i18nKey="join.process.meetingWhen"
                    components={{ calendar: <Link href={EVENTS_CALENDAR} target="_blank" rel="noopener" /> }}
                  />
                </Typography>
              ) : null}
            </li>
          ))}
        </Box>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
          {t('join.currentFees')}
        </Typography>
        {fees ? (
          <Stack component="dl" spacing={1} sx={{ m: 0 }}>
            {MEMBERSHIP_TYPES.map((type) => {
              const { annual, joining } = feesOf(fees, type)
              return (
                <Box key={type} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
                  <Box component="dt" sx={{ fontWeight: 600 }}>
                    {t(`join.types.${type}`)}:
                  </Box>
                  <Box component="dd" sx={{ m: 0 }}>
                    {t('join.feeLine', { annual: money(annual), joining: money(joining) })}
                  </Box>
                </Box>
              )
            })}
          </Stack>
        ) : (
          <Typography color="text.secondary">{t('join.feesUnavailable')}</Typography>
        )}
      </Box>

      <Alert severity="info" icon={<Icon icon="mdi:airplane" />}>
        {t('join.flyingRight')}
      </Alert>

      <Typography>
        {rulesLink ? <Trans i18nKey="join.readRules" components={{ rules: rulesLink }} /> : t('join.readRulesNoLink')}
      </Typography>

      <Typography>
        <Trans i18nKey="join.sil" components={{ sil: <Link href={SIL} target="_blank" rel="noopener" /> }} />
      </Typography>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, pt: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {info?.contactUrl ? (
            <Trans
              i18nKey="join.notJoining"
              components={{ contact: <Link href={info.contactUrl} target="_blank" rel="noopener" /> }}
            />
          ) : null}
        </Typography>
        <Button variant="contained" size="large" onClick={onStart} endIcon={<Icon icon="mdi:arrow-right" />}>
          {t('join.start')}
        </Button>
      </Stack>
    </Stack>
  )
}

function TypeOption({ type, fees, selected }: { type: MembershipType; fees: FeeSchedule | null; selected: boolean }) {
  const { t } = useTranslation()
  const money = useMoney()
  const amounts = fees ? feesOf(fees, type) : null
  return (
    <Box
      sx={{
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1,
        bgcolor: selected ? 'action.selected' : undefined,
      }}
    >
      <FormControlLabel
        value={type}
        control={<Radio />}
        sx={{ m: 0, width: '100%', alignItems: 'flex-start', '& .MuiRadio-root': { mt: -0.5 } }}
        label={
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{t(`join.types.${type}`)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`join.typeHint.${type}`)}
            </Typography>
            {amounts ? (
              <Typography variant="body2">
                {t('join.feeLine', { annual: money(amounts.annual), joining: money(amounts.joining) })}
              </Typography>
            ) : null}
          </Box>
        }
      />
    </Box>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
      <Typography component="legend" variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>{children}</Box>
    </Box>
  )
}

function Acceptance({
  checked,
  onChange,
  label,
  error,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: ReactNode
  error?: string
}) {
  return (
    <FormControl error={Boolean(error)} required>
      <FormControlLabel
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { mt: -0.75 } }}
        control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={label}
      />
      {error ? <FormHelperText sx={{ ml: 4 }}>{error}</FormHelperText> : null}
    </FormControl>
  )
}

function Nav({ onBack, children }: { onBack: () => void; children: ReactNode }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', pt: 1 }}>
      <Button onClick={onBack} startIcon={<Icon icon="mdi:arrow-left" />}>
        {t('join.back')}
      </Button>
      {children}
    </Stack>
  )
}

function Done({ email }: { email: string }) {
  const { t } = useTranslation()
  return (
    <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
      <Box sx={{ color: 'success.main' }}>
        <Icon icon="mdi:check-circle-outline" width={56} />
      </Box>
      <Typography variant="h6" component="h2">
        {t('join.doneTitle')}
      </Typography>
      <Typography>{t('join.doneBody', { email })}</Typography>
      <Alert severity="warning" sx={{ textAlign: 'left' }}>
        {t('join.doneVerify', { email })}
      </Alert>
      <Typography color="text.secondary">{t('join.doneBoard')}</Typography>
      <Button href={WEBSITE} variant="outlined">
        {t('join.toWebsite')}
      </Button>
    </Stack>
  )
}
